'use client'

// React Imports
import React, { useState, useEffect } from 'react'
import '@assets/iconify-icons/generated-icons.css'

// Next Imports
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'

import { useSession, signIn } from 'next-auth/react'

// MUI Imports
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Checkbox from '@mui/material/Checkbox'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Alert from '@mui/material/Alert'
import Collapse from '@mui/material/Collapse'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'

// Third-party Imports
import { Controller, useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { object, minLength, string, email, pipe, nonEmpty } from 'valibot'
import classnames from 'classnames'
import type { SubmitHandler } from 'react-hook-form'
import type { InferInput } from 'valibot'

// Server functions
import { isOnPremiseDeployment } from '@/libs/publicVars'

// Type Imports
import type { Mode } from '@core/types'

// Component Imports
import Logo from '@components/layout/shared/Logo'
import MSLogo from '@core/svg/Logo'

// View
import Register from '@views/Register'

// Hook Imports
import { useImageVariant } from '@core/hooks/useImageVariant'
import { useSettings } from '@core/hooks/useSettings'

// Form validation schema
const schema = object({
  email: pipe(string(), minLength(1, 'This field is required'), email('Please enter a valid email address')),
  password: pipe(
    string(),
    nonEmpty('This field is required'),
    minLength(5, 'Password must be at least 5 characters long')
  )
})

// Types
type FormData = InferInput<typeof schema>

interface ErrorMessage {
  message: string
  type?: 'error' | 'warning' | 'info' | 'success'
}

const Login = ({ mode }: { mode: Mode }) => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [viewMode, setViewMode] = useState<'signIn' | 'register'>('signIn')
  const [alertState, setAlertState] = useState<ErrorMessage | null>(null)
  const [alertOpen, setAlertOpen] = useState<boolean>(true)
  const [isOnPremise, setIsOnPremise] = useState<boolean>(false)

  // Images
  const darkIllustration = '/images/login-img.png'
  const lightIllustration = '/images/login-img.png'
  const borderedDarkIllustration = '/images/illustrations/auth/v2-login-dark-border.png'
  const borderedLightIllustration = '/images/illustrations/auth/v2-login-light-border.png'
  const darkSignup = '/images/signup-img.png'
  const lightSignup = '/images/signup-img.png'

  // Hooks
  const router = useRouter()
  const { settings } = useSettings()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: session, status } = useSession()
  const searchParams = useSearchParams()

  // Check if it's an on-premise deployment
  useEffect(() => {
    const checkOnPremise = async () => {
      try {
        const onPremise = await isOnPremiseDeployment()

        setIsOnPremise(onPremise)
      } catch (error) {
        console.error('Error checking on-premise status:', error)
        setIsOnPremise(false)
      }
    }

    checkOnPremise()
  }, [])

  // Form handling
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: valibotResolver(schema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  // Reset alert state when dependencies change
  useEffect(() => {
    if (alertState) {
      setAlertOpen(true)
    }
  }, [alertState])

  // Redirect if already authenticated
  useEffect(() => {
    if (status === 'loading' || status === 'unauthenticated') return

    if (status === 'authenticated') {
      const redirectTo = searchParams.get('redirectTo') || '/'

      window.location.href = redirectTo
    }
  }, [status, searchParams])

  // Image handling
  const characterIllustration = useImageVariant(
    mode,
    lightIllustration,
    darkIllustration,
    borderedLightIllustration,
    borderedDarkIllustration
  )

  const signupImage = useImageVariant(mode, lightSignup, darkSignup)

  // Event handlers
  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      setLoading(true)
      setAlertState(null)

      const res = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false
      })

      if (res?.ok) {
        const redirectTo = searchParams.get('redirectTo') || '/'

        window.location.href = redirectTo
      } else if (res?.error) {
        try {
          const errorData = JSON.parse(res.error)

          if (errorData.redirect && errorData.pendingToken) {
            await fetch('/api/auth/setPendingToken', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ pendingToken: errorData.pendingToken })
            })

            router.push(errorData.redirect) // Redirect to 2FA page
            return
          }

          setAlertState({
            message: errorData.message || 'Authentication failed',
            type: 'error'
          })
        } catch (e) {
          setAlertState({
            message: res.error || 'Invalid username or password',
            type: 'error'
          })
        }
      } else {
        setAlertState({
          message: 'An unexpected error occurred',
          type: 'error'
        })
      }
    } catch (error) {
      setAlertState({
        message: 'Connection error. Please try again later.',
        type: 'error'
      })
      console.error('Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  const redirectToSignUp = () => {
    if (isOnPremise) return
    setViewMode('register')
  }

  const redirectToSignIn = () => setViewMode('signIn')

  // Force to sign in view if on-premise
  useEffect(() => {
    if (isOnPremise && viewMode === 'register') {
      setViewMode('signIn')
    }
  }, [viewMode, isOnPremise])

  if (status === 'loading') {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {isLoading && (
        <div
          className='flex items-center justify-center gap-2 grow is-full'
          style={{ position: 'absolute', marginTop: '150px', zIndex: 1000 }}
        >
          <CircularProgress />
        </div>
      )}

      <div className='flex bs-full justify-center'>
        {/* Left side - illustration */}
        <div
          className={classnames(
            'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden align-middle overflow-hidden',
            {
              'border-ie': settings.skin === 'bordered'
            }
          )}
        >
          <div className='opacity-50'>
            <MSLogo className='w-full h-full blur-[150px] absolute top-0 left-0 rotate-45' />
          </div>
          <div className='plb-12 pis-12 w-[90%] h-[90%] relative'>
            <Image
              src={viewMode === 'signIn' ? characterIllustration : signupImage}
              layout='fill'
              objectFit='contain'
              alt='character-illustration'
              className='max-bs-[64vh] max-is-full bs-auto !top-1/2 !left-1/2 -translate-x-1/2 -translate-y-1/2'
            />
          </div>
        </div>

        {/* Right side - form content */}
        <div className='flex justify-center items-center bs-full bg-backgroundPaper border-l border-slate-500/10 !min-is-full p-6 xl:px-16 md:!min-is-[unset] md:p-12 md:is-[520px] shadow-md'>
          <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
            <div className='relative'>
              <Logo />
            </div>

            {viewMode === 'signIn' ? (
              <div>
                {/* Header */}
                <div className='mb-6'>
                  <Typography variant='h5'>{`Welcome! 👋🏻`}</Typography>
                  <Typography className='mb-2'>Please sign-in to your account</Typography>
                </div>

                {/* Error Alert */}
                {alertState && (
                  <Collapse in={alertOpen}>
                    <Alert
                      variant='outlined'
                      severity={alertState.type || 'error'}
                      action={
                        <IconButton aria-label='close' color='inherit' size='small' onClick={() => setAlertOpen(false)}>
                          <CloseIcon fontSize='inherit' />
                        </IconButton>
                      }
                      sx={{ mb: 4 }}
                    >
                      {alertState.message}
                    </Alert>
                  </Collapse>
                )}

                {/* Login Form */}
                <form noValidate onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
                  <Stack spacing={3}>
                    {/* Email Field */}
                    <Controller
                      name='email'
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          autoFocus
                          type='email'
                          InputLabelProps={{ shrink: true }}
                          label='Email'
                          placeholder='your.email@example.com'
                          disabled={isLoading}
                          error={!!errors.email}
                          helperText={errors.email?.message}
                        />
                      )}
                    />

                    {/* Password Field */}
                    <Controller
                      name='password'
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label='Password'
                          id='login-password'
                          type={isPasswordShown ? 'text' : 'password'}
                          InputLabelProps={{ shrink: true }}
                          disabled={isLoading}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  size='small'
                                  edge='end'
                                  onClick={handleClickShowPassword}
                                  onMouseDown={e => e.preventDefault()}
                                  aria-label='toggle password visibility'
                                >
                                  <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          error={!!errors.password}
                          helperText={errors.password?.message}
                        />
                      )}
                    />

                    {/* Remember Me & Forgot Password */}
                    <Box className='flex justify-between items-center flex-wrap gap-x-3 gap-y-1'>
                      <FormControlLabel
                        control={<Checkbox defaultChecked />}
                        label='Remember me'
                        disabled={isLoading}
                      />
                      {!isOnPremise && (
                        <Typography className='text-end' color='primary' component={Link} href='/reset-password-email'>
                          Forgot password?
                        </Typography>
                      )}
                    </Box>

                    {/* Submit Button */}
                    <Button disabled={isLoading} fullWidth variant='contained' type='submit' sx={{ mt: 2 }}>
                      {isLoading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : 'Log In'}
                    </Button>
                  </Stack>
                </form>

                {/* Sign Up Link */}
                {!isOnPremise && (
                  <Box mt={4}>
                    <Typography className='text-center' color='textSecondary'>
                      Don't have an account?{' '}
                      <Button variant='text' onClick={redirectToSignUp} sx={{ ml: 1 }} disabled={isLoading}>
                        Sign Up
                      </Button>
                    </Typography>
                  </Box>
                )}
              </div>
            ) : (
              !isOnPremise && <Register redirectToSignIn={redirectToSignIn} />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Login
